import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  useCreatePlan,
  useUpdateSubscriptionPlan,
  useSetPlanFeatures,
  type SubscriptionPlanRow,
} from "@/hooks/useSubscriptionPlans";
import { useFeatures, type FeatureRow } from "@/hooks/useFeatures";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: SubscriptionPlanRow | null; // null = create mode
}

const emptyForm = {
  code: "",
  name: "",
  description: "",
  priceMonthly: "",
  priceYearly: "",
  yearlyDiscountPct: "",
  maxBusinesses: "",
  maxRooms: "",
  trialDays: "",
  isDefault: false,
  isActive: true,
};

const toOptNumber = (v: string) => (v.trim() === "" ? undefined : Number(v));

export const PlanEditDialog = ({ open, onOpenChange, plan }: Props) => {
  const isEdit = !!plan;
  const features = useFeatures();
  const createPlan = useCreatePlan();
  const updatePlan = useUpdateSubscriptionPlan();
  const setFeatures = useSetPlanFeatures();

  const [form, setForm] = useState(emptyForm);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    if (!open) return;
    if (plan) {
      setForm({
        code: plan.code,
        name: plan.name,
        description: plan.description ?? "",
        priceMonthly: String(plan.priceMonthly ?? ""),
        priceYearly: plan.priceYearly == null ? "" : String(plan.priceYearly),
        yearlyDiscountPct:
          plan.yearlyDiscountPct == null ? "" : String(plan.yearlyDiscountPct),
        maxBusinesses:
          plan.maxBusinesses == null ? "" : String(plan.maxBusinesses),
        maxRooms: plan.maxRooms == null ? "" : String(plan.maxRooms),
        trialDays: plan.trialDays == null ? "" : String(plan.trialDays),
        isDefault: plan.isDefault,
        isActive: plan.isActive,
      });
      setSelectedFeatureIds(new Set(plan.features.map((f) => f.id)));
    } else {
      setForm(emptyForm);
      setSelectedFeatureIds(new Set());
    }
  }, [open, plan]);

  const grouped = useMemo(
    () => groupByCategory(features.data?.data ?? []),
    [features.data],
  );

  const toggleFeature = (id: string) => {
    setSelectedFeatureIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSubmit = async () => {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      priceMonthly: Number(form.priceMonthly),
      priceYearly: toOptNumber(form.priceYearly),
      yearlyDiscountPct: toOptNumber(form.yearlyDiscountPct),
      maxBusinesses: toOptNumber(form.maxBusinesses),
      maxRooms: toOptNumber(form.maxRooms),
      trialDays: toOptNumber(form.trialDays),
      isDefault: form.isDefault,
      isActive: form.isActive,
    };

    if (
      !payload.code ||
      !payload.name ||
      !Number.isFinite(payload.priceMonthly)
    ) {
      toast.error("Code, name and monthly price are required");
      return;
    }

    try {
      const saved = plan
        ? await updatePlan.mutateAsync({ id: plan.id, data: payload })
        : await createPlan.mutateAsync(payload);

      await setFeatures.mutateAsync({
        id: saved.id,
        data: { featureIds: Array.from(selectedFeatureIds) },
      });

      toast.success(plan ? "Plan updated" : "Plan created");
      onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const isSaving =
    createPlan.isPending || updatePlan.isPending || setFeatures.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit ${plan?.name}` : "Create plan"}
          </DialogTitle>
          <DialogDescription>
            Set name, pricing and which features are included. Commission rate
            is global (set in the section above).
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-3 -mr-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Code (slug)">
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="basic"
                disabled={isEdit}
              />
            </Field>
            <Field label="Name">
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Basic"
              />
            </Field>
            <Field label="Monthly price" className="md:col-span-1">
              <Input
                type="number"
                min={0}
                step={1}
                value={form.priceMonthly}
                onChange={(e) =>
                  setForm({ ...form, priceMonthly: e.target.value })
                }
              />
            </Field>
            <Field label="Yearly price (optional)">
              <Input
                type="number"
                min={0}
                step={1}
                value={form.priceYearly}
                onChange={(e) =>
                  setForm({ ...form, priceYearly: e.target.value })
                }
              />
            </Field>
            <Field label="Yearly discount %">
              <Input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={form.yearlyDiscountPct}
                onChange={(e) =>
                  setForm({ ...form, yearlyDiscountPct: e.target.value })
                }
              />
            </Field>
            <Field label="Trial days (plan override)">
              <Input
                type="number"
                min={0}
                value={form.trialDays}
                onChange={(e) =>
                  setForm({ ...form, trialDays: e.target.value })
                }
                placeholder="uses global default"
              />
            </Field>
            <Field label="Max businesses (blank = unlimited)">
              <Input
                type="number"
                min={0}
                value={form.maxBusinesses}
                onChange={(e) =>
                  setForm({ ...form, maxBusinesses: e.target.value })
                }
              />
            </Field>
            <Field label="Max rooms (blank = unlimited)">
              <Input
                type="number"
                min={0}
                value={form.maxRooms}
                onChange={(e) => setForm({ ...form, maxRooms: e.target.value })}
              />
            </Field>
          </div>

          <Field label="Description" className="mt-3">
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </Field>

          <div className="mt-3 flex items-center gap-6">
            <ToggleRow
              label="Default plan (auto-assigned on trial)"
              checked={form.isDefault}
              onChange={(v) => setForm({ ...form, isDefault: v })}
            />
            <ToggleRow
              label="Active (visible to subscribers)"
              checked={form.isActive}
              onChange={(v) => setForm({ ...form, isActive: v })}
            />
          </div>

          <div className="mt-5">
            <h4 className="font-semibold mb-2">Included features</h4>
            {features.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading features…</p>
            ) : grouped.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No features defined yet. Add some in the feature catalog below.
              </p>
            ) : (
              <div className="space-y-4">
                {grouped.map(({ category, items }) => (
                  <div key={category}>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                      {category}
                    </div>
                    <div className="space-y-1.5">
                      {items.map((f) => (
                        <label
                          key={f.id}
                          className="flex items-center justify-between gap-3 rounded-btn border border-border px-3 py-2 hover:bg-muted/40 cursor-pointer"
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">
                              {f.name}
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {f.code}
                            </div>
                          </div>
                          <Switch
                            checked={selectedFeatureIds.has(f.id)}
                            onCheckedChange={() => toggleFeature(f.id)}
                          />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSaving}>
            {isSaving ? "Saving…" : isEdit ? "Save changes" : "Create plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) => (
  <div className={`space-y-1 ${className ?? ""}`}>
    <Label>{label}</Label>
    {children}
  </div>
);

const ToggleRow = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer">
    <Switch checked={checked} onCheckedChange={onChange} />
    <span>{label}</span>
  </label>
);

const groupByCategory = (features: FeatureRow[]) => {
  const map = new Map<string, FeatureRow[]>();
  for (const f of features) {
    if (!map.has(f.category)) map.set(f.category, []);
    map.get(f.category)!.push(f);
  }
  return Array.from(map.entries())
    .map(([category, items]) => ({
      category,
      items: items.sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
      ),
    }))
    .sort((a, b) => a.category.localeCompare(b.category));
};

export default PlanEditDialog;
